import React from "react";
import { Box, Typography, Stack, Button, Link } from "@mui/material";
import { Calendar1, Clock, Building2, MapPin } from "lucide-react";
import { ROUTES } from "../../../config/routes";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { ValidateInvitationResponse } from "../services/invitationService";

interface InvitationDetailsProps {
  invitationData: ValidateInvitationResponse;
  onContinue: () => void;
}

export const InvitationDetails: React.FC<InvitationDetailsProps> = ({
  invitationData,
  onContinue,
}) => {
  const residentName = invitationData.residentName || "Residente";
  const expirationDate = invitationData.date ? new Date(invitationData.date) : null;

  return (
    <>
      <Box
        component="img"
        src="/images/DomusDesktop.png"
        alt="Domus logo"
        sx={{
          width: { xs: 200, sm: 250 },
          height: "auto",
          marginTop: 5,
          marginBottom: 7,
          display: "block",
          mx: "auto",
        }}
      />

      <Box sx={{ p: 1, mb: 4 }}>
        <Typography
          variant="h6"
          gutterBottom
          sx={{ fontWeight: 600, color: "#000000", mb: 1 }}
        >
          Hola,
        </Typography>
        <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
          <strong>{residentName}</strong> te ha invitado a realizar una visita.
        </Typography>

        {/* Fecha y hora */}
        <Typography
          variant="subtitle1"
          gutterBottom
          sx={{ fontWeight: 600, color: "#000000", mb: 1 }}
        >
          Fecha y hora
        </Typography>
        {expirationDate && (
          <Stack direction="column" spacing={1} mb={2}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Calendar1
                size={22}
                color="#666666"
                style={{ verticalAlign: "middle", marginBottom: 2 }}
              />
              <Typography variant="body2" sx={{ color: "#666666" }}>
                {format(expirationDate, "d MMMM, yyyy", { locale: es })}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <Clock
                size={22}
                color="#666666"
                style={{ verticalAlign: "middle", marginBottom: 2 }}
              />
              <Typography variant="body2" sx={{ color: "#666666" }}>
                {format(expirationDate, "h:mm a", { locale: es })}
              </Typography>
            </Stack>
          </Stack>
        )}

        {/* Lugar */}
        <Typography
          variant="subtitle1"
          gutterBottom
          sx={{ fontWeight: 600, color: "#000000", mb: 1 }}
        >
          Lugar
        </Typography>
        <Stack spacing={1.5}>
          {invitationData.apartmentNumber && (
            <Stack direction="row" spacing={1} alignItems="center">
              <Building2
                size={22}
                color="#666666"
                style={{ verticalAlign: "middle", marginBottom: 2 }}
              />
              <Typography variant="body2" sx={{ color: "#666666" }}>
                Departamento {invitationData.apartmentNumber}
              </Typography>
            </Stack>
          )}
          {invitationData.buildingName && (
            <Stack direction="row" spacing={1} alignItems="center">
              <MapPin
                size={22}
                color="#666666"
                style={{ verticalAlign: "middle", marginBottom: 2 }}
              />
              <Typography variant="body2" sx={{ color: "#666666" }}>
                {invitationData.buildingName}
              </Typography>
            </Stack>
          )}
        </Stack>
      </Box>

      {/* Botón continuar */}
      <Button
        onClick={onContinue}
        variant="contained"
        fullWidth
        sx={{
          bgcolor: "#000000",
          color: "#ffffff",
          borderRadius: "6px",
          py: 1.5,
          textTransform: "none",
          fontSize: "1rem",
          fontWeight: 600,
          boxShadow: "none",
          "&:hover": {
            bgcolor: "#333333",
            boxShadow: "none",
          },
        }}
      >
        Continuar
      </Button>

      {/* Términos y condiciones */}
      <Typography
        variant="caption"
        align="center"
        sx={{
          mt: 2,
          color: "#666666",
          display: "block",
        }}
      >
        Al registrarse, acepta nuestros{" "}
        <Link
          href={ROUTES.TERMS.trim()}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            color: "#000000",
            textDecoration: "underline",
            "&:hover": {
              color: "#333333",
            },
          }}
        >
          Términos de servicio
        </Link>{" "}
        y{" "}
        <Link
          href={ROUTES.PRIVACY.trim()}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            color: "#000000",
            textDecoration: "underline",
            "&:hover": {
              color: "#333333",
            },
          }}
        >
          Política de Privacidad
        </Link>
      </Typography>
    </>
  );
};
